import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { Trash2, Phone, Mail, Calendar, MessageSquare, Filter, RefreshCw, X } from "lucide-react";

const STATUS_LABELS = {
  new: { label: "Nou", color: "#c9a961", bg: "rgba(201,169,97,0.15)" },
  contacted: { label: "Contactat", color: "#3b82f6", bg: "rgba(59,130,246,0.15)" },
  converted: { label: "Convertit", color: "#22c55e", bg: "rgba(34,197,94,0.15)" },
  closed: { label: "Inchis", color: "#94a3b8", bg: "rgba(148,163,184,0.15)" },
};

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ new: 0, contacted: 0, converted: 0, closed: 0, total: 0 });
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [notesById, setNotesById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const r = await api.get("/admin/leads", { params });
      setItems(r.data.items || []);
      const s = await api.get("/admin/leads/stats");
      setStats(s.data || {});
    } catch (e) {
      window.alert(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const saveNotes = async (id) => {
    try {
      await api.patch(`/admin/leads/${id}`, { admin_notes: notesById[id] || "" });
      load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Sterge acest lead?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleString("ro-RO", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="admin-leads">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-serif text-3xl">Lead-uri (cerere oferta)</h1>
        <button onClick={load} className="btn-ghost inline-flex items-center gap-2" data-testid="leads-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Reincarca
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "total", label: "Total" },
          { key: "new", label: "Nou" },
          { key: "contacted", label: "Contactat" },
          { key: "converted", label: "Convertit" },
          { key: "closed", label: "Inchis" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key === "total" ? "" : s.key)}
            className={`card-surface p-4 text-left transition ${filter === (s.key === "total" ? "" : s.key) ? "ring-1 ring-[var(--brand-primary)]" : ""}`}
            data-testid={`leads-filter-${s.key}`}
          >
            <div className="text-2xl font-serif" style={{ color: s.key !== "total" ? STATUS_LABELS[s.key]?.color : "var(--brand-primary)" }}>
              {stats[s.key] || 0}
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>
              {s.label}
            </div>
          </button>
        ))}
      </div>

      {filter && (
        <div className="flex items-center gap-2 text-xs" style={{ color: "var(--brand-text-secondary)" }}>
          <Filter className="w-3 h-3" />
          Filtru: {STATUS_LABELS[filter]?.label || filter}
          <button onClick={() => setFilter("")} className="ml-1 inline-flex items-center" data-testid="leads-clear-filter">
            <X className="w-3 h-3" /> Sterge
          </button>
        </div>
      )}

      {/* List */}
      {items.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm" style={{ color: "var(--brand-text-secondary)" }} data-testid="leads-empty">
          Niciun lead {filter ? `cu statusul "${STATUS_LABELS[filter]?.label}"` : "inca"}.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((lead) => {
            const open = expanded === lead.id;
            const sl = STATUS_LABELS[lead.status] || STATUS_LABELS.new;
            return (
              <div key={lead.id} className="card-surface overflow-hidden" data-testid={`lead-item-${lead.id}`}>
                <div className="p-4 flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{lead.name}</span>
                      <span
                        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                        style={{ color: sl.color, background: sl.bg }}
                      >
                        {sl.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs flex-wrap" style={{ color: "var(--brand-text-secondary)" }}>
                      <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-[var(--brand-primary)]">
                        <Phone className="w-3 h-3" />{lead.phone}
                      </a>
                      {lead.email && (
                        <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-[var(--brand-primary)]">
                          <Mail className="w-3 h-3" />{lead.email}
                        </a>
                      )}
                      {(lead.pickup_date || lead.return_date) && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />{lead.pickup_date || "?"} → {lead.return_date || "?"}
                        </span>
                      )}
                      <span className="ml-auto">{formatDate(lead.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => { setExpanded(open ? null : lead.id); setNotesById((n) => ({ ...n, [lead.id]: lead.admin_notes || "" })); }}
                      className="btn-ghost text-xs px-3 py-1.5"
                      data-testid={`lead-toggle-${lead.id}`}
                    >
                      {open ? "Inchide" : "Detalii"}
                    </button>
                    <button
                      onClick={() => remove(lead.id)}
                      className="p-1.5 hover:bg-white/5 rounded"
                      title="Sterge"
                      data-testid={`lead-delete-${lead.id}`}
                    >
                      <Trash2 className="w-4 h-4" style={{ color: "var(--brand-text-secondary)" }} />
                    </button>
                  </div>
                </div>

                {open && (
                  <div className="border-t px-4 py-4 space-y-3 text-sm" style={{ borderColor: "var(--brand-border)", background: "rgba(255,255,255,0.02)" }}>
                    {lead.car_preference && (
                      <div>
                        <span className="label-eyebrow text-[10px]">Masina preferata</span>
                        <div>{lead.car_preference}</div>
                      </div>
                    )}
                    {lead.message && (
                      <div>
                        <span className="label-eyebrow text-[10px] flex items-center gap-1"><MessageSquare className="w-3 h-3" />Mesaj</span>
                        <div className="whitespace-pre-line">{lead.message}</div>
                      </div>
                    )}
                    <div>
                      <span className="label-eyebrow text-[10px]">Sursa</span>
                      <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{lead.source || "-"}</div>
                    </div>
                    <div>
                      <span className="label-eyebrow text-[10px]">Status</span>
                      <div className="flex gap-2 mt-1">
                        {Object.entries(STATUS_LABELS).map(([k, v]) => (
                          <button
                            key={k}
                            onClick={() => setStatus(lead.id, k)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition ${lead.status === k ? "" : "opacity-60 hover:opacity-100"}`}
                            style={{
                              borderColor: lead.status === k ? v.color : "var(--brand-border)",
                              background: lead.status === k ? v.bg : "transparent",
                              color: lead.status === k ? v.color : "var(--brand-text-secondary)",
                            }}
                            data-testid={`lead-status-${lead.id}-${k}`}
                          >
                            {v.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="label-eyebrow text-[10px]">Notite interne</span>
                      <textarea
                        value={notesById[lead.id] ?? lead.admin_notes ?? ""}
                        onChange={(e) => setNotesById((n) => ({ ...n, [lead.id]: e.target.value }))}
                        rows={3}
                        className="form-input mt-1 resize-none w-full text-sm"
                        placeholder="Adauga notite despre conversatia cu clientul..."
                        data-testid={`lead-notes-${lead.id}`}
                      />
                      <button
                        onClick={() => saveNotes(lead.id)}
                        className="btn-ghost text-xs mt-2"
                        data-testid={`lead-save-notes-${lead.id}`}
                      >
                        Salveaza notite
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
